import { format } from 'date-fns'
import { useSelector } from 'react-redux'
import { useIntl, defineMessages } from 'react-intl'
import { RootState } from '../../../../types/rootState'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { OpenHouseAddToCalendarButton } from './OpenHouseAddToCalendarButton'
import { ScheduleTourButton } from '../ScheduleTour/ScheduleTourButton/ScheduleTourButton'
import { Card, Flex, Grid, Icon, Text, Heading, CardBody, GridItem } from '@homebotapp/hb-react-component-catalog'

export const MSG = defineMessages({
  agentTour: {
    id: 'ListingDetails.ScheduleTour.agentTour',
    defaultMessage: 'Tour with your preferred agent'
  },
  attendOpenHouse: {
    id: 'ListingDetails.ScheduleTour.attendOpenHouse',
    defaultMessage: 'Attend an open house'
  },
  seeInPerson: {
    id: 'ListingDetails.ScheduleTour.seeInPerson',
    defaultMessage: 'Ready to see it in person?'
  },
  refreshments: {
    id: 'ListingDetails.ScheduleTour.refreshments',
    defaultMessage: 'Refreshments'
  },
  noteFromAgent: {
    id: 'ListingDetails.ScheduleTour.noteFromAgent',
    defaultMessage: 'Notes from agent:'
  }
})

export interface OpenHouseOrScheduleTourProps {
  listing?: Listing
}

export const OpenHouseOrScheduleTour = ({ listing }: OpenHouseOrScheduleTourProps) => {
  const intl = useIntl()
  const isPublic = useSelector<RootState, boolean>(state => state.auth.isPublic)
  const futureOpenHouses = listing?.openHouses?.filter(openHouse => new Date(openHouse.endTime) > new Date())

  if (isPublic && !futureOpenHouses?.length) {
    return null
  }

  return (
    <>
      {/* @ts-ignore */}
      <Card mb={4} size='lg' bgColor='primary.50 !important'>
        <CardBody p={4}>
          <Heading pb={2} borderBottom='1px solid var(--hb-colors-primary-300)' mb={0} size='2xs' textAlign='left'>
            {intl.formatMessage(MSG.seeInPerson)}
          </Heading>
          {futureOpenHouses?.length > 0 && (
            <Flex mt={2}>
              <Flex align='center'>
                <Flex w={7} h={7} align='center' justify='center' bg='primary.300' borderRadius='50%'>
                  <Icon name='opendoor' color='primary.700' />
                </Flex>
                <Heading mb={0} ml={2} size='3xs' variant='eyebrow'>
                  {intl.formatMessage(MSG.attendOpenHouse)}
                </Heading>
              </Flex>
            </Flex>
          )}
          {futureOpenHouses?.length > 0 && (
            <Grid templateColumns={['repeat(1, 1fr)', null, 'repeat(2, 1fr)']} gap={1} mt={2}>
              {futureOpenHouses.map(openHouse => (
                <GridItem>
                  <Card
                    p={2}
                    mr={2}
                    key={openHouse.id}
                    // @ts-ignore
                    bgColor='primary.200 !important'
                    border='1px solid var(--hb-colors-primary-300)'
                  >
                    {openHouse?.startTime && (
                      <Heading mb={0} size='xs' fontWeight='bold'>
                        {format(new Date(openHouse.startTime?.replace(' ', 'T')), 'E M/d')}
                      </Heading>
                    )}
                    {openHouse?.startTime && openHouse?.endTime && (
                      <Text size='2xs' mb={1} textAlign='center'>
                        {format(new Date(openHouse.startTime?.replace(' ', 'T')), 'h:mm a')} -{' '}
                        {format(new Date(openHouse.endTime?.replace(' ', 'T')), 'h:mm a')}
                      </Text>
                    )}
                    {openHouse.refreshments && (
                      <Flex px={2} borderRadius={8} bg='primary.300' align='center' justify='center'>
                        <Icon h={3} name='forkandknife' color='primary.700' />
                        <Text mb={0} fontWeight='bold' size='2xs' color='primary.700'>
                          {intl.formatMessage(MSG.refreshments)}
                        </Text>
                      </Flex>
                    )}
                    {openHouse.remarks && (
                      <>
                        <Text mt={1} mb={0} size='2xs' textAlign='center' transform='uppercase' fontWeight='bold'>
                          {intl.formatMessage(MSG.noteFromAgent)}
                        </Text>
                        <Text size='2xs' textAlign='center'>
                          {openHouse.remarks}
                        </Text>
                      </>
                    )}
                    <OpenHouseAddToCalendarButton listing={listing} openHouse={openHouse} />
                  </Card>
                </GridItem>
              ))}
            </Grid>
          )}
          {!isPublic && (
            <Flex direction='column'>
              <Flex align='center' my={2}>
                <Flex w={7} h={7} align='center' justify='center' bg='primary.300' borderRadius='50%'>
                  <Icon name='briefcase' color='primary.700' />
                </Flex>
                <Heading mb={0} ml={2} size='3xs' variant='eyebrow'>
                  {intl.formatMessage(MSG.agentTour)}
                </Heading>
              </Flex>
              <ScheduleTourButton />
            </Flex>
          )}
        </CardBody>
      </Card>
    </>
  )
}
